import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '../components/ui/button';
import { ArrowLeft, Lock, CheckCircle2, Star, Trophy } from 'lucide-react';

const LadderMode = () => {
  const navigate = useNavigate();

  // Sample ladder data - this should come from your backend
  const ladderData = React.useMemo(() => ({
    Physics: [
      { id: 1, name: 'Mechanics - Easy', difficulty: 'Easy', locked: false, completed: true, score: 85 },
      { id: 2, name: 'Mechanics - Medium', difficulty: 'Medium', locked: false, completed: true, score: 72 },
      { id: 3, name: 'Mechanics - Hard', difficulty: 'Hard', locked: false, completed: false, score: null },
      { id: 4, name: 'Electromagnetism - Easy', difficulty: 'Easy', locked: true, completed: false, score: null },
      { id: 5, name: 'Electromagnetism - Medium', difficulty: 'Medium', locked: true, completed: false, score: null },
      { id: 6, name: 'Electromagnetism - Hard', difficulty: 'Hard', locked: true, completed: false, score: null },
    ],
    Chemistry: [
      { id: 7, name: 'Atomic Structure - Easy', difficulty: 'Easy', locked: false, completed: true, score: 90 },
      { id: 8, name: 'Atomic Structure - Medium', difficulty: 'Medium', locked: false, completed: false, score: null },
      { id: 9, name: 'Atomic Structure - Hard', difficulty: 'Hard', locked: true, completed: false, score: null },
    ],
    Biology: [
      { id: 10, name: 'Cell Biology - Easy', difficulty: 'Easy', locked: false, completed: false, score: null },
      { id: 11, name: 'Cell Biology - Medium', difficulty: 'Medium', locked: true, completed: false, score: null },
      { id: 12, name: 'Cell Biology - Hard', difficulty: 'Hard', locked: true, completed: false, score: null },
    ],
  }), []);

  const [selectedSubject, setSelectedSubject] = useState('Physics');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );

  // Generate nodes and edges for the selected subject
  useEffect(() => {
    const levels = ladderData[selectedSubject];
    const newNodes = levels.map((level, index) => ({
      id: `node-${level.id}`,
      type: 'default',
      position: { x: 250, y: index * 150 + 50 },
      data: {
        label: (
          <div className="p-4 min-w-[280px]">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">{level.name}</h3>
              {level.completed && <CheckCircle2 className="w-5 h-5 text-green-500" />}
              {level.locked && <Lock className="w-5 h-5 text-gray-400" />}
            </div>
            <div className="flex items-center justify-between">
              <span className={`text-xs px-2 py-1 rounded ${level.difficulty === 'Easy' ? 'bg-green-500/20 text-green-600' :
                level.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-600' :
                  'bg-red-500/20 text-red-600'
                }`}>
                {level.difficulty}
              </span>
              {level.score !== null && (
                <span className="text-xs font-medium flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                  {level.score}%
                </span>
              )}
              {!level.locked && !level.completed && (
                <Button size="sm" className="text-xs h-7">
                  Start
                </Button>
              )}
            </div>
          </div>
        ),
      },
      style: {
        background: level.locked ? '#f3f4f6' : level.completed ? '#ecfdf5' : '#fff',
        border: level.locked ? '2px solid #d1d5db' : level.completed ? '2px solid #10b981' : '2px solid #3b82f6',
        borderRadius: '12px',
        padding: 0,
      },
    }));

    const newEdges = levels.slice(0, -1).map((level, index) => ({
      id: `edge-${level.id}-${levels[index + 1].id}`,
      source: `node-${level.id}`,
      target: `node-${levels[index + 1].id}`,
      animated: !levels[index + 1].locked,
      style: { stroke: levels[index + 1].locked ? '#d1d5db' : '#3b82f6' },
    }));

    setNodes(newNodes);
    setEdges(newEdges);
  }, [selectedSubject, setNodes, setEdges, ladderData]);

  return (
    <div className="min-h-screen pt-20 pb-12 px-6">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/challenge')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Challenges
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Ladder Mode</h1>
              <p className="text-muted-foreground">
                Progress through levels from easy to hard. Complete each level to unlock the next!
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <Trophy className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-600">245 Points</span>
            </div>
          </div>
        </div>

        {/* Subject Tabs */}
        <div className="flex gap-2 mb-6">
          {Object.keys(ladderData).map((subject) => (
            <Button
              key={subject}
              variant={selectedSubject === subject ? 'default' : 'outline'}
              onClick={() => setSelectedSubject(subject)}
              className="flex-1"
            >
              {subject}
            </Button>
          ))}
        </div>

        {/* Flow Diagram */}
        <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ height: '600px' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            fitView
            attributionPosition="bottom-left"
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
          </ReactFlow>
        </div>

        {/* Info Panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="stat-card text-center">
            <div className="text-2xl font-bold text-green-600">3</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
          <div className="stat-card text-center">
            <div className="text-2xl font-bold text-blue-600">2</div>
            <div className="text-sm text-muted-foreground">Available</div>
          </div>
          <div className="stat-card text-center">
            <div className="text-2xl font-bold text-gray-400">4</div>
            <div className="text-sm text-muted-foreground">Locked</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LadderMode;
